'use client'

// PhotoMode — take a proper picture of him.
//
// Opens with P or the camera button. Everything else on screen goes away, the
// view gets masked to an Instagram crop so you're composing the actual shot
// you'll post, and the shutter saves a PNG. You can still walk while it's
// open, which matters: framing a photo is mostly moving your feet.

import { useCallback, useEffect, useRef, useState } from 'react'
import useGameStore from '../../stores/gameStore'
import { PHOTO_ASPECTS, requestPhoto, downloadPhoto } from '../../lib/photo/capture'
import { ui } from '../../lib/ui/theme'

export function PhotoMode() {
  const photoMode = useGameStore((s) => s.photoMode)
  const setPhotoMode = useGameStore((s) => s.setPhotoMode)
  const [aspectId, setAspectId] = useState('portrait')
  const [flash, setFlash] = useState(false)
  const [saved, setSaved] = useState(null)   // last shot, shown as a thumbnail
  const busy = useRef(false)

  const aspect = PHOTO_ASPECTS.find((a) => a.id === aspectId) || PHOTO_ASPECTS[0]

  const shoot = useCallback(async () => {
    if (busy.current) return
    busy.current = true
    setFlash(true)
    setTimeout(() => setFlash(false), 170)
    const url = await requestPhoto(aspect.ratio)
    if (url) {
      setSaved(url)
      const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, '-')
      downloadPhoto(url, `pino-${stamp}.png`)
    }
    busy.current = false
  }, [aspect])

  // P opens and closes, Esc closes, Space is the shutter once you're in.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target && /INPUT|TEXTAREA|SELECT/.test(e.target.tagName)) return
      if (e.key === 'p' || e.key === 'P') {
        e.preventDefault()
        useGameStore.getState().togglePhotoMode()
        return
      }
      if (!useGameStore.getState().photoMode) return
      if (e.key === 'Escape') setPhotoMode(false)
      if (e.code === 'Space' || e.key === ' ') { e.preventDefault(); shoot() }
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [setPhotoMode, shoot])

  // Drop the thumbnail when the mode closes so reopening is a clean slate.
  useEffect(() => { if (!photoMode) setSaved(null) }, [photoMode])

  if (!photoMode) return null

  return (
    <>
      {/* Crop mask. Pointer-events off so you can still walk while framing. */}
      <div className="fixed inset-0 z-40 pointer-events-none flex items-center justify-center">
        <div
          style={{
            // The window minus the crop = the dimmed surround, drawn as a huge
            // shadow so there's no maths on four separate letterbox bars.
            aspectRatio: aspect.ratio ? String(aspect.ratio) : undefined,
            height: aspect.ratio ? 'min(88vh, 96vw / (' + aspect.ratio + '))' : '100%',
            width: aspect.ratio ? undefined : '100%',
            maxWidth: '96vw',
            boxShadow: aspect.ratio ? '0 0 0 100vmax rgba(10, 8, 7, 0.72)' : 'none',
            border: aspect.ratio ? '1px solid rgba(255, 238, 222, 0.28)' : 'none',
            borderRadius: 2,
          }}
        />
      </div>

      {/* Shutter flash */}
      {flash && (
        <div
          className="fixed inset-0 z-50 pointer-events-none"
          style={{ background: 'rgba(255,255,255,0.62)' }}
        />
      )}

      {/* Controls */}
      <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 pointer-events-auto flex items-center gap-3">
        <div
          className="flex items-center gap-1 p-1"
          style={{
            background: ui.panel,
            border: ui.border,
            borderRadius: ui.radiusPill,
            backdropFilter: ui.blur,
            WebkitBackdropFilter: ui.blur,
            fontFamily: ui.font,
          }}
        >
          {PHOTO_ASPECTS.map((a) => (
            <button
              key={a.id}
              type="button"
              onClick={() => setAspectId(a.id)}
              style={{
                padding: '5px 12px',
                borderRadius: ui.radiusPill,
                border: 'none',
                cursor: 'pointer',
                fontSize: ui.size.xs,
                fontFamily: ui.font,
                background: a.id === aspectId ? ui.accent : 'transparent',
                color: a.id === aspectId ? '#2a1e14' : ui.textSoft,
                fontWeight: a.id === aspectId ? 700 : 500,
              }}
            >
              {a.label}
            </button>
          ))}
        </div>

        {/* Shutter */}
        <button
          type="button"
          onClick={shoot}
          aria-label="take a photo"
          style={{
            width: 62,
            height: 62,
            borderRadius: '50%',
            border: '3px solid rgba(255, 249, 242, 0.9)',
            background: 'rgba(255, 249, 242, 0.22)',
            backdropFilter: ui.blur,
            WebkitBackdropFilter: ui.blur,
            cursor: 'pointer',
            boxShadow: ui.shadow,
          }}
        />

        <button
          type="button"
          onClick={() => setPhotoMode(false)}
          style={{
            padding: '8px 16px',
            borderRadius: ui.radiusPill,
            background: ui.panel,
            border: ui.border,
            backdropFilter: ui.blur,
            WebkitBackdropFilter: ui.blur,
            color: ui.textSoft,
            fontSize: ui.size.xs,
            fontFamily: ui.font,
            cursor: 'pointer',
          }}
        >
          done
        </button>
      </div>

      {/* Last shot, so you can see it saved without leaving the world. */}
      {saved && (
        <div
          className="fixed bottom-6 right-6 z-50 pointer-events-none"
          style={{
            padding: 5,
            background: ui.panel,
            border: ui.border,
            borderRadius: 10,
            backdropFilter: ui.blur,
            WebkitBackdropFilter: ui.blur,
            boxShadow: ui.shadow,
          }}
        >
          <img src={saved} alt="" style={{ display: 'block', width: 84, borderRadius: 6 }} />
          <div
            style={{
              fontSize: 10,
              fontFamily: ui.font,
              color: ui.textSoft,
              textAlign: 'center',
              paddingTop: 4,
            }}
          >
            saved
          </div>
        </div>
      )}
    </>
  )
}

export default PhotoMode
